import type { LeaseCompFieldRule } from "@/features/survey/types/survey.core.types";
import type { LeaseComp } from "@/features/survey/types/survey.leaseComp.types";
import { useGetLeaseCompFieldRulesQuery } from "@/services/leaseCompFieldRules";
import { useGetLeaseCompsQuery } from "@/services/leaseComps";
import { useSurvey } from "@/shared/hooks/useSurveySelectors";

export interface UseLeaseCompDataReturn {
  leaseCompFieldRules: LeaseCompFieldRule[] | undefined;
  leaseComps: LeaseComp[] | undefined;
  isLoading: boolean;
  error: any;
  isVisible: boolean;
}

/**
 * Hook to manage loading of lease comp data
 * Fetches lease comp field rules and lease comps when surveyId is available
 *
 * Data flow:
 * 1. Triggers both API queries as soon as surveyId is available
 * 2. RTK Query handles caching and deduplication automatically
 * 3. Returns combined loading/error state and fetched data
 *
 * Caching:
 * - RTK Query handles caching automatically
 * - Data refetches when surveyId changes
 * - Subsequent calls use cached data
 * - Mutations (reorder, toggle isActive) invalidate cache automatically
 *
 * Note: APIs are called regardless of whether the "lease comps" section is hidden
 * This ensures data is always available for components that need it
 *
 * @returns {UseLeaseCompDataReturn} Object containing field rules, lease comps, loading state, errors, and section visibility flag
 */
export const useLeaseCompData = (): UseLeaseCompDataReturn => {
  const survey = useSurvey();

  // Detect if "lease comps" section exists and is visible (for reference/monitoring)
  const isLeaseCompSectionVisible =
    survey?.sections?.some(
      (s) => s.name?.toLowerCase() === "lease comps" && !s.isHidden,
    ) ?? false;

  // Get surveyId for queries
  const surveyId = survey?.id;

  // Fetch lease comp field rules (skip only if no surveyId)
  const {
    data: leaseCompFieldRules,
    isLoading: isFieldRulesLoading,
    error: fieldRulesError,
  } = useGetLeaseCompFieldRulesQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
  });

  // Fetch lease comps (skip only if no surveyId)
  const {
    data: leaseComps,
    isLoading: isLeaseCompsLoading,
    error: leaseCompsError,
  } = useGetLeaseCompsQuery(surveyId || "", {
    skip: !surveyId,
    refetchOnMountOrArgChange: false,
  });

  return {
    leaseCompFieldRules,
    leaseComps,
    isLoading: isFieldRulesLoading || isLeaseCompsLoading,
    error: fieldRulesError || leaseCompsError,
    isVisible: isLeaseCompSectionVisible,
  };
};
